import React, { useState } from 'react';
import {
  BottomNavigation,
  BottomNavigationAction,
  AppBar,
  Toolbar,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import {
  Home,
  Event,
  Map,
  AccountCircle,
  Search,
  Add,
  Assignment,
  List,
  CalendarToday,
  Psychology as AIIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import NotificationBell from './NotificationBell';
import { useLanguage } from '../hooks/useLanguage';
import AISearchComponent from './AISearchComponent';
import { Activity } from '../types/Activity';

interface NavigationBarProps {
  currentView: string;
  onViewChange: (view: string) => void;
  onSearchOpen?: () => void;
  onMapView?: () => void;
  onCreateActivity?: () => void;
  onActivitySelect?: (activity: Activity) => void;
}

const NavigationBar: React.FC<NavigationBarProps> = ({
  currentView,
  onViewChange,
  onSearchOpen,
  onMapView,
  onCreateActivity,
  onActivitySelect,
}) => {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // AI Search state
  const [showAISearch, setShowAISearch] = useState(false);
  const [profileMenuAnchor, setProfileMenuAnchor] = React.useState<null | HTMLElement>(null);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setProfileMenuAnchor(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileMenuAnchor(null);
  };

  const handleViewChange = (view: string) => {
    onViewChange(view);
    if (view === 'map' && onMapView) {
      onMapView();
    }
  };

  // Custom theme colors
  const customColors = {
    primary: '#6A4C93',
    secondary: '#1982C4',
    success: '#8AC926',
    warning: '#FFCA3A',
    error: '#FF595E',
  };

  if (isMobile) {
    // Mobile Bottom Navigation - Only main navigation items
    return (
      <>
        <BottomNavigation
          value={currentView}
          onChange={(_event, newValue) => handleViewChange(newValue)}
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            backgroundColor: customColors.primary,
            borderTop: `2px solid ${customColors.secondary}`,
            '& .MuiBottomNavigationAction-root': {
              color: 'rgba(255, 255, 255, 0.7)',
              minWidth: 'auto', // Allow items to fit better
              '&.Mui-selected': {
                color: customColors.warning,
              },
              '& .MuiBottomNavigationAction-label': {
                fontSize: '0.65rem',
                fontWeight: 500,
              },
            },
          }}
        >
          <BottomNavigationAction
            label={t('navigation.home')}
            value="home"
            icon={<Home />}
          />
          <BottomNavigationAction
            label={t('navigation.activities')}
            value="activities"
            icon={<List />}
          />
          <BottomNavigationAction
            label={t('navigation.calendar')}
            value="calendar"
            icon={<CalendarToday />}
          />
          <BottomNavigationAction
            label={t('navigation.map')}
            value="map"
            icon={<Map />}
          />
          <BottomNavigationAction
            label={t('navigation.profile')}
            value="profile"
            icon={<AccountCircle />}
          />
        </BottomNavigation>

        {/* AI Search Dialog */}
        <Dialog
          open={showAISearch}
          onClose={() => setShowAISearch(false)}
          fullScreen={isMobile}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: { xs: 0, sm: 2 },
              height: { xs: '100%', sm: '90vh' },
              maxHeight: { xs: '100%', sm: '90vh' },
              display: 'flex',
              flexDirection: 'column',
            },
          }}
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AIIcon />
              AI Search
            </Typography>
            <IconButton onClick={() => setShowAISearch(false)} aria-label="Close AI search">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ flexGrow: 1, p: 0 }}>
            <AISearchComponent
              onSearchResults={(results, query) => {
                console.log('🤖 AI Search completed:', { 
                  query, 
                  resultsCount: results.count 
                });
              }}
              onActivitySelect={(activity) => {
                onActivitySelect?.(activity);
                setShowAISearch(false);
              }}
              showSuggestions={true}
              compact={false}
            />
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Desktop Top Navigation
  return (
    <AppBar 
      position="fixed"
      sx={{ 
        background: `linear-gradient(135deg, ${customColors.primary}, ${customColors.secondary})`,
        boxShadow: `0 4px 20px rgba(106, 76, 147, 0.3)`,
        zIndex: 1100,
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between' }}>
        {/* Left side - Logo and Navigation */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography
            variant="h6"
            sx={{ 
              fontWeight: 'bold',
              color: 'white',
              mr: 4,
              fontSize: '1.5rem',
            }}
          >
            TreffPunkts
          </Typography>

          {/* Navigation Items */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Box
              onClick={() => handleViewChange('home')}
              sx={{
                px: 3,
                py: 1.5,
                borderRadius: 2,
                cursor: 'pointer',
                backgroundColor: currentView === 'home' ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <Home sx={{ fontSize: 20 }} />
              <Typography sx={{ fontWeight: 500 }}>Home</Typography>
            </Box>

            <Box
              onClick={() => handleViewChange('activities')}
              sx={{
                px: 3,
                py: 1.5,
                borderRadius: 2,
                cursor: 'pointer',
                backgroundColor: currentView === 'activities' ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <List sx={{ fontSize: 20 }} />
              <Typography sx={{ fontWeight: 500 }}>Activities</Typography>
            </Box>

            <Box
              onClick={() => handleViewChange('calendar')}
              sx={{
                px: 3,
                py: 1.5,
                borderRadius: 2,
                cursor: 'pointer',
                backgroundColor: currentView === 'calendar' ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <CalendarToday sx={{ fontSize: 20 }} />
              <Typography sx={{ fontWeight: 500 }}>Calendar</Typography>
            </Box>

            <Box
              onClick={() => handleViewChange('map')}
              sx={{
                px: 3,
                py: 1.5,
                borderRadius: 2,
                cursor: 'pointer',
                backgroundColor: currentView === 'map' ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <Map sx={{ fontSize: 20 }} />
              <Typography sx={{ fontWeight: 500 }}>Map</Typography>
            </Box>
          </Box>
        </Box>

        {/* Right side - Actions and Profile */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Search Button */}
          {onSearchOpen && (
            <IconButton
              color="inherit"
              onClick={onSearchOpen}
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                },
              }}
            >
              <Search />
            </IconButton>
          )}

          {/* Create Activity Button */}
          <Box
            onClick={onCreateActivity}
            sx={{
              px: 2,
              py: 1,
              borderRadius: 2,
              backgroundColor: customColors.success,
              color: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              fontWeight: 500,
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: '#7ab020',
                transform: 'translateY(-1px)',
              },
            }}
          >
            <Add sx={{ fontSize: 18 }} />
            Create Activity
          </Box>

          {/* Notifications */}
          <NotificationBell size="medium" />

          {/* Profile */}
          {user && (
            <IconButton
              color="inherit"
              onClick={handleProfileMenuOpen}
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                },
              }}
            >
              {user.photoURL ? (
                <Avatar 
                  src={user.photoURL} 
                  sx={{ width: 32, height: 32 }}
                />
              ) : (
                <AccountCircle />
              )}
            </IconButton>
          )}
        </Box>
      </Toolbar>

      {/* Profile Menu */}
      <Menu
        anchorEl={profileMenuAnchor}
        open={Boolean(profileMenuAnchor)}
        onClose={handleProfileMenuClose}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 200,
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          },
        }}
      >
        <MenuItem onClick={() => { 
          handleViewChange('profile'); 
          handleProfileMenuClose(); 
        }}>
          <ListItemIcon>
            <AccountCircle />
          </ListItemIcon>
          <ListItemText primary={t('navigation.profile')} />
        </MenuItem>
        <MenuItem onClick={() => {
          onViewChange('my-activities');
          handleProfileMenuClose();
        }}>
          <ListItemIcon>
            <Event />
          </ListItemIcon>
          <ListItemText primary={t('navigation.myActivities')} />
        </MenuItem>
        <MenuItem onClick={() => {
          onViewChange('my-participations');
          handleProfileMenuClose();
        }}>
          <ListItemIcon>
            <Assignment />
          </ListItemIcon>
          <ListItemText primary={t('navigation.myParticipations')} />
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => {
          // TODO: Add Settings navigation when implemented
          console.log('Settings navigation - to be implemented');
          handleProfileMenuClose();
        }}>
                              <ListItemText primary={t('profile:titles.settings')} />
        </MenuItem>
        <MenuItem onClick={async () => {
          try {
            await logout();
            handleProfileMenuClose();
          } catch (error) {
            console.error('Logout failed:', error);
            handleProfileMenuClose();
          }
        }}>
          <ListItemText primary={t('navigation.logout')} />
        </MenuItem>
      </Menu>
    </AppBar>
  );
};

export default NavigationBar; 